<template>
  <div class="op-page">
    <div class="op-wrapper">
      <!-- Pulsing spinner -->
      <div class="op-spinner-area">
        <div
          v-if="orderStatus === STATUSES.PENDING"
          class="op-pulse-ring"
        />
        <div
          class="op-spinner-circle"
          :class="{
            'op-spinner-circle--pending': orderStatus === STATUSES.PENDING,
            'op-spinner-circle--confirmed': orderStatus === STATUSES.CONFIRMED,
            'op-spinner-circle--failed': orderStatus === STATUSES.FAILED,
          }"
        >
          <ekitabu-icon
            v-if="orderStatus === STATUSES.CONFIRMED"
            size="36"
            color="white"
          >
            mdi-check
          </ekitabu-icon>
          <ekitabu-icon
            v-else-if="orderStatus === STATUSES.FAILED"
            size="36"
            color="white"
          >
            mdi-close
          </ekitabu-icon>
          <div
            v-else
            class="op-dots"
          >
            <span class="op-dot" />
            <span class="op-dot" />
            <span class="op-dot" />
          </div>
        </div>
      </div>

      <!-- Title -->
      <h1 class="op-title">
        {{ $t("checkout.orderPending.orderStatus") }}
      </h1>

      <!-- Status chip -->
      <ekitabu-chip
        :class="`${chipColor}-gradient rounded-lg justify-center text-caption font-weight-medium op-chip ekitabu-chip`"
        text-color="textOnPrimary"
      >
        {{ orderStatus }}
      </ekitabu-chip>

      <!-- Progress bar (pending only) -->
      <div
        v-if="orderStatus === STATUSES.PENDING"
        class="op-progress-track"
      >
        <div class="op-progress-bar" />
      </div>

      <!-- Failure message -->
      <div
        v-if="orderStatus == STATUSES.FAILED"
        class="op-message-card"
      >
        <p class="op-message-label">
          {{ $t("checkout.orderPending.safaricomDeclined") }}:
        </p>
        <p class="op-message-detail">
          {{ safaricomMpesaRequest?.resultDescription ?? momoMtnPayment?.resultDescription ?? $t("checkout.orderPending.unknownError") }}
        </p>
      </div>

      <!-- Admin spoof confirm -->
      <div
        v-if="isAdmin && (orderStatus === STATUSES.PENDING || orderStatus === STATUSES.FAILED)"
        class="op-spoof-card"
      >
        <p class="op-spoof-label">
          Admin: Spoof confirm this order
        </p>
        <div class="op-spoof-input-row">
          <input
            v-model="spoofPhone"
            class="op-spoof-input"
            placeholder="Phone (e.g. 254708374149)"
            type="text"
          >
          <button
            class="op-spoof-btn"
            :disabled="spoofLoading"
            @click="spoofConfirm"
          >
            {{ spoofLoading ? 'Confirming...' : 'Confirm' }}
          </button>
        </div>
        <p
          v-if="spoofError"
          class="op-spoof-error"
        >
          {{ spoofError }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import BookQueries from "@/graphql/BookQueries";
import OrderQueries from "@/graphql/OrderQueries";
import AdminCheckMixin from "@/mixins/AdminCheckMixin";
import gql from "graphql-tag";
import iris from "@/iris";

export default {
  name: "OrderPending",
  metaInfo() {
    return {
      title: this.$t('routes.orderPending'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('seo.orderPendingDescription'),
        },
      ],
    }
  },
  mixins: [AdminCheckMixin],
  data() {
    return {
      order: null,
      safaricomMpesaRequestId: null,
      momoMtnRequestId: null,
      spoofPhone: "",
      spoofLoading: false,
      spoofError: null,
    };
  },
  computed: {
    orderStatus() {
      if (this.momoMtnRequestId) {
        if (this.momoMtnPayment?.resultCode == "0") {
          return this.STATUSES.CONFIRMED;
        } else if (this.momoMtnPayment?.resultCode == "1") {
          return this.STATUSES.FAILED;
        }
        return this.STATUSES.PENDING;
      }
      else if (this.safaricomMpesaRequestId) {
        if (this.safaricomMpesaRequest?.resultCode) {

          const resultCodeInteger = parseInt(
            this.safaricomMpesaRequest.resultCode
          );
          if (resultCodeInteger === 0) {
            return this.STATUSES.CONFIRMED;
          } else if (resultCodeInteger > 0) {
            return this.STATUSES.FAILED;
          }
        }
      }

      return this.STATUSES.PENDING;
    },
    safaricomMpesaRequest() {
      if (this.order && this.order.safaricomMpesaRequests.length == 1) {
        return this.order.safaricomMpesaRequests[0];
      } else if (this.order && this.order.safaricomMpesaRequests.length > 1) {
        throw new Error(
          "We weren't able to filter out to one safaricomMpesaRequest"
        );
      }
      return null;
    },
    STATUSES() {
      return {
        PENDING: this.$t("checkout.orderPending.statuses.pending")+'...',
        CONFIRMED: this.$t("checkout.orderPending.statuses.confirmed"),
        FAILED: this.$t("checkout.orderPending.statuses.failed"),
      };
    },
    chipColor() {
      if (this.orderStatus == this.STATUSES.PENDING) {
        return "unemphasized";
      } else if (this.orderStatus == this.STATUSES.CONFIRMED) {
        return "tertiary";
      } else if (this.orderStatus == this.STATUSES.FAILED) {
        return "warning";
      }
      return "";
    },
  },
  apollo: {
    // TODO: Get the mercure integration working in order to dynamically get updates on the order status
    //  https://github.com/ekitabu-LLC/shop-web/issues/53
    order: {
      query: gql`
        query getOrderStatus($id: ID!, $safaricomMpesaRequestId: String) {
          order(id: $id) {
            id
            confirmed
            safaricomMpesaRequests(id: $safaricomMpesaRequestId) {
              id
              resultCode
              resultDescription
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
          safaricomMpesaRequestId: this.safaricomMpesaRequestId,
        };
      },
      async result({ data }) {
        if (data.order.confirmed) {
          // Fetch full order details
          const orderId = this.$route.params.id;
          const { data: orderData } = await this.$apollo.query({
            query: OrderQueries.getOrder,
            variables: { orderId },
            fetchPolicy: "network-only",
          });

          const orderDetails  = orderData.order;

          // --- Google Analytics Purchase Event ---
          window.gtag && window.gtag('event', 'purchase', {
            transaction_id: orderDetails.id,
            value: Number(orderDetails.totalPrice - orderDetails.totalTax),
            tax: Number(orderDetails.totalTax),
            coupon: orderDetails.orderCoupon?.code || undefined,
            currency: orderDetails.currency?._id,
            items: orderDetails.orderLines?.edges?.map(({ node }) => ({
              item_id: node.productId,
              item_name: node.name,
              price: Number(node.price),
            })),
          });
          this.navigateToConfirmation();
        }
      },
      pollInterval: 1500,
      skip() {
        return !this.safaricomMpesaRequestId;
      },
    },
    momoMtnPayment: {
      query: gql`
        query updateStatusMomoMtnPayment($id: ID!) {
          updateStatusMomoMtnPayment(id: $id) {
            id
            resultCode
            resultDescription
            transactionReference
            localOrder {
              id
              confirmed
            }
          }
        }
      `,
      variables() {
        return {
          id: this.momoMtnRequestId,
        };
      },
      update(data) {
        return data?.updateStatusMomoMtnPayment;
      },
      async result({ data }) {
        this.momoMtnStatus = data?.updateStatusMomoMtnPayment?.resultCode;
        this.momoMtnStatusDescription = data?.updateStatusMomoMtnPayment?.resultDescription;

        if (data?.updateStatusMomoMtnPayment?.localOrder?.confirmed) {
          this.navigateToConfirmation();
        }
      },
      pollInterval: 1500,
      skip() {
        return !this.momoMtnRequestId;
      },
    }
  },
  beforeDestroy() {
    if (this.$apollo.queries.order) {
      this.$apollo.queries.order.stopPolling();
    }
    if (this.$apollo.queries.momoMtnPayment) {
      this.$apollo.queries.momoMtnPayment.stopPolling();
    }
  },
  mounted() {
    // by default, the named route params are correct on navigating from checkout, but not if the url is reloaded
    // so checks are needed to make sure the params for mpesa/momo are handled correctly
    const paramSafaricomMpesaRequestId = this.$route.params?.safaricomMpesaRequestId;
    const paramMomoMtnRequestId = this.$route.params?.momoMtnRequestId;

    if (paramSafaricomMpesaRequestId?.includes(iris.safaricomMpesaRequests)) {
      this.safaricomMpesaRequestId = paramSafaricomMpesaRequestId;
    }
    else if (paramMomoMtnRequestId?.includes(iris.safaricomMpesaRequests)) {
      this.safaricomMpesaRequest = paramMomoMtnRequestId;
    }

    if (paramMomoMtnRequestId?.includes(iris.momoMtnPayments)) {
      this.momoMtnRequestId = paramMomoMtnRequestId;
    }
    else if (paramSafaricomMpesaRequestId?.includes(iris.momoMtnPayments)) {
      this.momoMtnRequestId = paramSafaricomMpesaRequestId;
    }
  },
  methods: {
    async spoofConfirm() {
      this.spoofLoading = true;
      this.spoofError = null;
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation spoofConfirmOrder($id: ID!, $phoneNumber: String) {
              spoofConfirmOrder(input: { id: $id, phoneNumber: $phoneNumber }) {
                order {
                  id
                  confirmed
                }
              }
            }
          `,
          variables: {
            id: this.$route.params.id,
            phoneNumber: this.spoofPhone || null,
          },
        });
      } catch (e) {
        this.spoofError = e.message;
      } finally {
        this.spoofLoading = false;
      }
    },
    async navigateToConfirmation() {
      const isBuyNow = this.$route.query.flow === 'buyNow';

      if (isBuyNow) {
        // Guest Buy Now — no bookshelf to refetch, clear Buy Now state
        this.$store.commit("clearBuyNow");
      } else {
        // Standard cart flow — refetch bookshelf and clear cart
        await this.$apollo.query({
          query: BookQueries.getUserBookshelf,
          fetchPolicy: "network-only",
        });
        this.$store.commit("setCurrentCart", null);
      }

      this.$router.push({
        name: "confirmed",
        params: {
          id: this.$route.params.id,
        },
        query: isBuyNow ? { flow: 'buyNow' } : {},
      });
    },
  }
};
</script>

<style lang="scss" scoped>
/* ── Page ── */
.op-page {
  min-height: 100vh;
  background: white;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 16px 80px;
  font-family: $book-font-body;
}

.op-wrapper {
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* ── Spinner area ── */
.op-spinner-area {
  position: relative;
  width: 96px;
  height: 96px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Pulsing ring behind the circle */
.op-pulse-ring {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: $book-brand-gradient;
  opacity: 0.18;
  animation: op-pulse 2s $book-ease-out infinite;
}

@keyframes op-pulse {
  0% {
    transform: scale(1);
    opacity: 0.18;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.06;
  }
  100% {
    transform: scale(1);
    opacity: 0.18;
  }
}

/* Circle */
.op-spinner-circle {
  position: relative;
  z-index: $z-base;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.4s $book-ease-out;
}

.op-spinner-circle--pending {
  background: $book-brand-gradient;
  box-shadow: 0 4px 24px rgba($book-red-dark, 0.32);
}

.op-spinner-circle--confirmed {
  background: $book-green;
  box-shadow: 0 4px 24px rgba($book-green, 0.28);
}

.op-spinner-circle--failed {
  background: linear-gradient(135deg, darken($book-red-danger, 15%) 0%, lighten($book-red-danger, 10%) 100%);
  box-shadow: 0 4px 24px rgba($book-red-danger, 0.28);
}

/* Bouncing dots for pending state */
.op-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}

.op-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  animation: op-bounce 1.4s ease-in-out infinite;
}

.op-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.op-dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes op-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ── Title ── */
.op-title {
  font-family: $book-font-display;
  font-style: italic;
  font-weight: 600;
  font-size: 1.75rem;
  line-height: 1.25;
  color: $book-ink;
  margin: 0 0 16px;
}

/* ── Chip override ── */
.op-chip {
  margin-bottom: 24px;
  font-family: $book-font-body;
  font-weight: 600;
}

/* ── Progress track ── */
.op-progress-track {
  width: 100%;
  max-width: 320px;
  height: 4px;
  border-radius: 2px;
  background: $book-grey-lighter;
  overflow: hidden;
  margin-bottom: 32px;
}

.op-progress-bar {
  width: 40%;
  height: 100%;
  border-radius: 2px;
  background: $book-brand-gradient;
  animation: op-slide 1.8s ease-in-out infinite;
}

@keyframes op-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

/* ── Failure message card ── */
.op-message-card {
  width: 100%;
  background: $book-grey-lightest;
  border: 1px solid $book-grey-lighter;
  border-radius: 12px;
  padding: 20px 24px;
  text-align: left;
  margin-top: 8px;
}

.op-message-label {
  font-family: $book-font-body;
  font-size: 0.875rem;
  color: $book-grey;
  margin: 0 0 6px;
}

.op-message-detail {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  font-weight: 700;
  color: $book-ink;
  margin: 0;
}

/* ── Admin spoof card ── */
.op-spoof-card {
  width: 100%;
  background: $book-grey-lightest;
  border: 2px dashed $book-grey-lighter;
  border-radius: 12px;
  padding: 20px 24px;
  margin-top: 24px;
  text-align: left;
}

.op-spoof-label {
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $book-grey;
  margin: 0 0 12px;
}

.op-spoof-input-row {
  display: flex;
  gap: 8px;
}

.op-spoof-input {
  flex: 1;
  font-family: $book-font-body;
  font-size: 0.9375rem;
  padding: 10px 14px;
  border: 1px solid $book-grey-lighter;
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: $book-grey;
  }
}

.op-spoof-btn {
  font-family: $book-font-body;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: $book-brand-gradient;
  color: var(--v-textOnPrimary-base);
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.op-spoof-error {
  font-family: $book-font-body;
  font-size: 0.8125rem;
  color: $book-red-danger;
  margin: 8px 0 0;
}

/* ── Responsive ── */
@media (max-width: 899px) {
  .op-page {
    padding: 56px 16px 64px;
  }

  .op-title {
    font-size: 1.375rem;
  }

  .op-spinner-area {
    width: 80px;
    height: 80px;
    margin-bottom: 24px;
  }

  .op-spinner-circle {
    width: 60px;
    height: 60px;
  }

  .op-message-card {
    padding: 16px 18px;
  }
}
</style>
